import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Users,
  TrendingUp,
  AlertCircle,
  Plus,
  Activity,
  Search,
  UserPlus,
  ClipboardList,
  ArrowRight,
  Loader2,
  Calendar,
  Clock,
  AlertTriangle,
  TrendingDown,
  Minus
} from 'lucide-react';
import { normalizeSupportAreaArray } from '../constants/supportAreas';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { canEdit, isAdmin, isSuperAdmin, selectedSchoolId } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, [selectedSchoolId]); // Refetch when school changes

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await apiService.getDashboard(selectedSchoolId);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="h-3.5 w-3.5 text-green-600" />;
      case 'declining': return <TrendingDown className="h-3.5 w-3.5 text-red-600" />;
      case 'flat': return <Minus className="h-3.5 w-3.5 text-amber-600" />;
      default: return null;
    }
  };

  const formatReviewDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const today = new Date();
    const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return `${Math.abs(diffDays)} days overdue`;
    } else if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Tomorrow';
    } else if (diffDays <= 7) {
      return `In ${diffDays} days`;
    } else {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#2C4C3B]" />
      </div>
    );
  }

  const showEmptyState = stats?.total_active_students === 0;
  const hasOverdueReviews = stats?.overdue_reviews?.length > 0;
  const hasUpcomingReviews = stats?.upcoming_reviews?.length > 0;

  return (
    <div className="page-container" data-testid="dashboard-page">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Overview of student progress and interventions</p>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions flex-wrap">
        {canEdit && (
          <>
            <Button 
              className="bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl" 
              data-testid="add-student-btn"
              onClick={() => navigate(selectedSchoolId ? `/students/new?school_id=${selectedSchoolId}` : '/students/new')}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add Student
            </Button>
          </>
        )}
        <Link to="/students">
          <Button variant="outline" className="rounded-xl border-[#E5E5E5] hover:border-[#2C4C3B] hover:bg-[#2C4C3B]/5" data-testid="view-students-btn">
            <Users className="h-4 w-4 mr-2" />
            View All Students
          </Button>
        </Link>
      </div>

      {showEmptyState ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-icon">
              <Users size={32} />
            </div>
            <h3 className="empty-title">No students yet</h3>
            <p className="empty-text">
              Get started by adding your first student.
            </p>
            <div className="flex gap-3 justify-center">
              <Button 
                className="bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl" 
                data-testid="empty-add-student-btn"
                onClick={() => navigate(selectedSchoolId ? `/students/new?school_id=${selectedSchoolId}` : '/students/new')}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Student
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Overdue Reviews Alert */}
          {hasOverdueReviews && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl" data-testid="overdue-alert">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-red-800 mb-1">Overdue Reviews</h3>
                  <p className="text-sm text-red-700 mb-2">
                    {stats.overdue_reviews.length} student{stats.overdue_reviews.length > 1 ? 's have' : ' has'} overdue review dates.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {stats.overdue_reviews.slice(0, 3).map((student) => (
                      <Link
                        key={student.id}
                        to={`/students/${student.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-white rounded-full text-sm text-red-700 hover:bg-red-100"
                      >
                        {student.full_name}
                        <span className="text-xs text-red-500">({formatReviewDate(student.review_date)})</span>
                      </Link>
                    ))}
                    {stats.overdue_reviews.length > 3 && (
                      <Link to="/students?review_status=overdue" className="text-sm text-red-600 hover:underline font-medium">
                        +{stats.overdue_reviews.length - 3} more
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stats Grid */}
          <div className="stats-grid" data-testid="stats-grid">
            <Link 
              to="/students?active=true" 
              className="stat-card cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:border-[#2C4C3B] transition-all duration-200"
              data-testid="stat-active-students"
            >
              <div className="stat-icon primary">
                <Users size={24} />
              </div>
              <div className="stat-value" data-testid="total-students">{stats?.total_active_students || 0}</div>
              <div className="stat-label">Active Students</div>
              <div className="text-xs text-[#2C4C3B] mt-1 opacity-0 group-hover:opacity-100">Click to view →</div>
            </Link>

            <Link 
              to="/students?tier=Tier+1" 
              className="stat-card cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:border-green-500 transition-all duration-200"
              data-testid="stat-tier1"
            >
              <div className="stat-icon success">
                <TrendingUp size={24} />
              </div>
              <div className="stat-value" data-testid="tier1-count">{stats?.tier1_count || 0}</div>
              <div className="stat-label">Tier 1 Students</div>
            </Link>

            <Link 
              to="/students?tier=Tier+2" 
              className="stat-card cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:border-amber-400 transition-all duration-200"
              data-testid="stat-tier2"
            >
              <div className="stat-icon warning">
                <Activity size={24} />
              </div>
              <div className="stat-value" data-testid="tier2-count">{stats?.tier2_count || 0}</div>
              <div className="stat-label">Tier 2 Students</div>
            </Link>

            <Link 
              to="/students?tier=Tier+3" 
              className="stat-card cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:border-red-400 transition-all duration-200"
              data-testid="stat-tier3"
            >
              <div className="stat-icon danger">
                <AlertCircle size={24} />
              </div>
              <div className="stat-value" data-testid="tier3-count">{stats?.tier3_count || 0}</div>
              <div className="stat-label">Tier 3 Students</div>
            </Link>

            <Link 
              to="/students?status=Needs+Review" 
              className="stat-card cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:border-blue-400 transition-all duration-200"
              data-testid="stat-needs-review"
            >
              <div className="stat-icon info">
                <Search size={24} />
              </div>
              <div className="stat-value" data-testid="needs-review-count">{stats?.needs_review_count || 0}</div>
              <div className="stat-label">Needs Review</div>
            </Link>
          </div>

          {/* Three Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Students Needing Attention */}
            <div className="card">
              <div className="card-header flex justify-between items-center">
                <h3 className="card-title">Students Needing Attention</h3>
                <Link to="/students?status=Needs+Review" className="text-[#2C4C3B] text-sm font-medium hover:underline">
                  View All
                </Link>
              </div>
              <div className="card-content">
                {stats?.students_needing_attention?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.students_needing_attention.map((student) => (
                      <Link
                        key={student.id}
                        to={`/students/${student.id}`}
                        className="student-card"
                        data-testid={`attention-student-${student.id}`}
                      >
                        <div className="student-avatar">
                          {student.full_name?.charAt(0) || 'S'}
                        </div>
                        <div className="student-info">
                          <div className="student-name flex items-center gap-2">
                            {student.full_name}
                            {getTrendIcon(student.trend)}
                          </div>
                          <div className="student-meta">
                            {Array.isArray(student.concern_area) 
                              ? normalizeSupportAreaArray(student.concern_area).join(', ') 
                              : student.concern_area}
                          </div>
                        </div>
                        <span className={`badge ${
                          student.status === 'Needs Review' ? 'badge-review' :
                          student.status === 'Limited Progress' ? 'badge-limited' :
                          'badge-improving'
                        }`}>
                          {student.status}
                        </span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#595959] text-sm text-center py-8">
                    No students currently need review
                  </p>
                )}
              </div>
            </div>

            {/* Upcoming Reviews */}
            <div className="card">
              <div className="card-header flex justify-between items-center">
                <h3 className="card-title flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Upcoming Reviews
                </h3>
                <Link to="/students" className="text-[#2C4C3B] text-sm font-medium hover:underline">
                  View All
                </Link>
              </div>
              <div className="card-content">
                {hasUpcomingReviews ? (
                  <div className="space-y-3">
                    {stats.upcoming_reviews.slice(0, 5).map((student) => {
                      const daysUntil = Math.ceil((new Date(student.review_date) - new Date()) / (1000 * 60 * 60 * 24));
                      const isUrgent = daysUntil <= 3;
                      
                      return (
                        <Link
                          key={student.id}
                          to={`/students/${student.id}`}
                          className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F3F4F1] transition-colors"
                          data-testid={`upcoming-review-${student.id}`}
                        >
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            isUrgent ? 'bg-amber-100 text-amber-700' : 'bg-[#F3F4F1] text-[#595959]'
                          }`}>
                            <Clock className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{student.full_name}</div>
                            <div className="text-xs text-[#595959]">{student.review_date}</div>
                          </div>
                          <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                            isUrgent ? 'bg-amber-100 text-amber-700' : 'bg-[#F3F4F1] text-[#595959]'
                          }`}>
                            {formatReviewDate(student.review_date)}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[#595959] text-sm text-center py-8">
                    No upcoming reviews in the next 2 weeks
                  </p>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="card">
              <div className="card-header flex justify-between items-center">
                <h3 className="card-title">Recent Progress Data</h3>
              </div>
              <div className="card-content">
                {stats?.recent_progress?.length > 0 ? (
                  <div className="space-y-0">
                    {stats.recent_progress.map((progress) => (
                      <div key={progress.id} className="activity-item" data-testid={`activity-${progress.id}`}>
                        <div className="activity-icon">
                          <TrendingUp size={18} />
                        </div>
                        <div className="activity-content">
                          <div className="activity-text">
                            <span className="font-medium">{progress.student_name}</span>
                            {' '}scored{' '}
                            <span className="font-medium">{progress.score} {progress.score_unit}</span>
                          </div>
                          <div className="activity-time">
                            {progress.date_collected} • {progress.measure_name}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#595959] text-sm text-center py-8">
                    No recent progress data
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
